import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouterPathEnum } from "../../constants/router";
import { IApiResponse } from "../../interfaces/common/IApiResponse";
import { postDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { useAuth } from "../../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import { actionTypes } from "../../store/actionTypes/actionTypes";
import { COMMON } from "../../constants/common";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import React from "react";
import { FixedSizeList } from "react-window";
import { isAfter, isBefore } from "date-fns";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment-timezone";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"; // or AdapterDateFns / AdapterMoment based on your preference
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import { IUserDetailsPermissionState } from "interfaces/store/IUserDetailsPermissionState";
import { hasPermission } from "../../utils/permissionUtil";
import { PERMISSION } from "../../constants/userPermission";

interface Meeting {
  Agenda: string;
  Category: string;
  EndTime: string;
  FromDate: string;
  Fullday: boolean;
  Id: number;
  MeetingId: string;
  MeetingUrl: string;
  Passcode: string;
  StartTime: string;
  Tag: string;
  Title: string;
  ToDate: string;
}

type MeetingStatus = "Future" | "Active" | "Past";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateUserRole } = useAuth();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredMeetings, setFilteredMeetings] = useState<Meeting[]>([]);

  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [meetingState, setMeetingState] = useState("");
  const [formData, setFormData] = useState({
    searchText: "",
    meetingState: "",
  });


  const getUserDetails = async () => {
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_USER_DETAILS}`;
    const payloadData = null;

    postDataFromApi<any>(
      url,
      payloadData,
      (response: IApiResponse<any>) => {
        updateUserRole(response.data.userDetails.roleName);
        setUserDetails(response.data.userDetails);
        dispatch({
          type: actionTypes.UPDATE_USER_DETAILS,
          payload: response?.data?.userDetails,
        });
        dispatch({
          type: actionTypes.UPDATE_USER_PERMISSION,
          payload: response?.data?.role_permission,
        });
        localStorage.setItem(
          COMMON.USER_DETAILS_LS_KEY.DETAILS,
          JSON.stringify(response.data?.userDetails)
        );
        localStorage.setItem(
          COMMON.USER_DETAILS_LS_KEY.ROLE_PERMISSION,
          JSON.stringify(response.data?.role_permission)
        );
      },
      { isAuthRequired: true },
      navigate
      // Error handling (including UserDoesNotExists and UserInactive) is handled by apiService
    );
  };

  const fetchMeetings = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_MEETINGS}`;
      const payloadData = {};

      await postDataFromApi<any>(
        url,
        payloadData,
        (response: IApiResponse<any>) => {
          console.log("Meetings response:", response.data.data);
          if (response.data.status === "Success" && response.data.data) {
            const meetings = response.data.data;
            setMeetings(meetings);
            setFilteredMeetings(meetings);
          } else {
            setError("Failed to fetch meetings");
          }
        },
        { isAuthRequired: true },
        navigate
      );
    } catch (err) {
      console.error("Error fetching meetings:", err);
      setError("An error occurred while fetching meetings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMeetingState("Active");
    getUserDetails();
    fetchMeetings();
  }, []);

  const toLocalDateTime = (dateStr: string, timeStr: string): Date => {
    const utcDate = new Date(`${dateStr}T${timeStr}Z`);
    return utcDate;
  };
  const formatLocalDate = (dateStr: string, timeStr: string): string => {
    const date = toLocalDateTime(dateStr, timeStr);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatMeetingTime = (meeting: any): string => {
    const fromDate = new Date(`${meeting.FromDate}T${meeting.StartTime}:00Z`); // Convert UTC to local time
    const toDate = new Date(`${meeting.ToDate}T${meeting.EndTime}:00Z`); // Convert UTC to local time

    // Convert both times to the local time of the user
    const localFromTime = fromDate.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // 12-hour format with AM/PM
    });

    const localToTime = toDate.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // 12-hour format with AM/PM
    });

    return `${localFromTime} - ${localToTime}`;
  };

  const getMeetingStatus = (meeting: Meeting): MeetingStatus => {
    const now = new Date();

    // Parse the meeting dates and times
    const startDateTime = new Date(
      `${meeting.FromDate}T${meeting.StartTime}:00Z`
    ); // Convert UTC to local time
    const endDateTime = new Date(`${meeting.ToDate}T${meeting.EndTime}:00Z`);

    // Determine status
    if (isAfter(startDateTime, now)) {
      return "Future";
    } else if (isBefore(endDateTime, now)) {
      return "Past";
    } else {
      return "Active";
    }
  };

  const getStatusColor = (status: MeetingStatus): string => {
    switch (status) {
      case "Future":
        return "#2196f3"; // Blue
      case "Active":
        return "#4caf50"; // Green
      case "Past":
        return "#9e9e9e"; // Gray
      default:
        return "#9e9e9e";
    }
  };

  const applyFilters = (
    searchText: string,
    stateFilter: string,
    startDate: Date | null,
    endDate: Date | null
  ) => {
    const start = startDate ? moment(startDate, "YYYY-MM-DD") : null;
    const end = endDate ? moment(endDate, "YYYY-MM-DD") : null;

    const filtered = meetings.filter((meeting) => {
      const fromDate = moment(meeting.FromDate, "YYYY-MM-DD");
      const toDate = moment(meeting.ToDate, "YYYY-MM-DD");

      // Search Filter
      const matchesSearch = searchText
        ? meeting.Title.toLowerCase().includes(searchText.toLowerCase()) ||
        meeting.Agenda.toLowerCase().includes(searchText.toLowerCase()) ||
        meeting.Category.toLowerCase().includes(searchText.toLowerCase()) ||
        meeting.MeetingId.toLowerCase().includes(searchText.toLowerCase())
        : true;

      // State Filter
      const matchesState = stateFilter
        ? getMeetingStatus(meeting) === stateFilter
        : true;

      // Date Filter
      let matchesDate = true;
      if (start && end) {
        const isFromInRange = fromDate.isBetween(start, end, "day", "[]");
        const isToInRange = toDate.isBetween(start, end, "day", "[]");
        matchesDate = isFromInRange || isToInRange;
      } else if (start && !end) {
        matchesDate = fromDate.isSame(start, "day");
      } else if (!start && end) {
        matchesDate = toDate.isSame(end, "day");
      }

      return matchesSearch && matchesState && matchesDate;
    });

    setFilteredMeetings(filtered);
  };

  const handleSearch = () => {
    applyFilters(
      formData.searchText,
      meetingState,
      filterStartDate,
      filterEndDate
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleStateChange = (event: SelectChangeEvent) => {
    const selectedState =
      event.target.value === "all" ? "" : event.target.value;
    setMeetingState(selectedState);
    handleChange("meetingState", selectedState);
    applyFilters(
      formData.searchText,
      selectedState,
      filterStartDate,
      filterEndDate
    );
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearSearch = () => {
    setFormData({
      searchText: "",
      meetingState: "",
    });
    setFilterStartDate(null);
    setFilterEndDate(null);
    setMeetingState("");
    setFilteredMeetings(meetings);
    applyFilters("", "", null, null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fetchMeetings();
  };

  useEffect(() => {
    setFilteredMeetings(meetings);
    applyFilters(
      formData.searchText,
      meetingState,
      filterStartDate,
      filterEndDate
    );
  }, [meetings]);

  const dateFilter = (field: string, value: any) => {
    const newStartDate = field === "startDate" ? value : filterStartDate;
    const newEndDate = field === "endDate" ? value : filterEndDate;

    setFilterStartDate(newStartDate);
    setFilterEndDate(newEndDate);

    applyFilters(formData.searchText, meetingState, newStartDate, newEndDate);
  };

  const handleCopyMeetingId = (meeting: Meeting) => {
    const textToCopy = `${meeting.MeetingId}_${meeting.Id}`;
    navigator.clipboard.writeText(textToCopy);
  };

  const userPermissions = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.rolePermission
  );


  return (
    <Box className="dashboard-container">
      <form id="meeting-dashboard-form" onSubmit={handleSubmit}>
        <Box
          sx={{
            mb: 2,
            p: 3,
            marginTop: 10,
            marginBottom: 5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <TextField
              id="searchText"
              label="Search Text"
              variant="outlined"
              value={formData.searchText}
              sx={{ maxWidth: 500, width: "100%", minWidth: 300, mr: 2 }}
              onChange={(e) => handleChange("searchText", e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSearch}
                      edge="end"
                      sx={{ color: "primary.main" }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* <Box display="flex" gap={2} mb={2}> */}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={filterStartDate}
                onChange={(newDate: unknown) => {
                  const formattedDate = newDate
                    ? dayjs(newDate as Dayjs).format("YYYY-MM-DD")
                    : null;
                  dateFilter("startDate", formattedDate);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="To"
                value={filterEndDate}
                onChange={(newDate: unknown) => {
                  const formattedDate = newDate
                    ? dayjs(newDate as Dayjs).format("YYYY-MM-DD")
                    : null;
                  dateFilter("endDate", formattedDate);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
            {/* </Box> */}

            <FormControl sx={{ minWidth: 120, width: "100%", maxWidth: 200 }}>
              <InputLabel id="State">State</InputLabel>
              <Select
                labelId="State"
                id="selState"
                value={meetingState || "all"}
                label="State"
                sx={{ width: "100%" }}
                onChange={handleStateChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Future">Future</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Past">Past</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              sx={{ minWidth: 125 }}
              onClick={handleClearSearch}
            >
              Clear All
            </Button>
          </Stack>
        </Box>
      </form>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : filteredMeetings.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Typography>No meetings found</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: "66vh",
            width: "100vw",
            maxWidth: "100vw",
            overflowX: "hidden",
            overflowY: "auto",
            pb: 4,
          }}
        >
          <AutoSizer>
            {({ height, width }: Size) => (
              <FixedSizeList
                height={height}
                width={width}
                itemCount={Math.ceil(filteredMeetings.length / 3)}
                itemSize={181}
              >
                {({ index, style }) => {
                  const visibleMeetings = filteredMeetings.filter(() => {
                    if (userDetails) {
                      return true;
                    }
                    return true;
                  });

                  // Slice the visible meetings per row
                  const rowMeetings = visibleMeetings.slice(
                    index * 3,
                    index * 3 + 3
                  );

                  return (
                    <Box
                      style={style}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 2,
                        px: 2,
                        boxSizing: "border-box",
                      }}
                    >
                      {rowMeetings.map((meeting, i) => {
                        const status = getMeetingStatus(meeting);
                        const statusColor = getStatusColor(status);

                        return (
                          <Box key={i}>
                            <Card
                              elevation={3}
                              key={meeting.Id}
                              sx={{
                                mb: 1,
                                p: 2,
                                cursor: "pointer",
                                "&:hover": {
                                  boxShadow: 6,
                                  transform: "translateY(-4px)",
                                  transition: "all 0.3s ease-in-out",
                                },
                              }}
                              onClick={() => {
                                navigate(
                                  `/${RouterPathEnum.DGO}/${RouterPathEnum.HOME}/${meeting.Id}/${meeting.MeetingId}`
                                );
                                window.location.reload();
                              }}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      mb: 0,
                                      fontSize: "1rem",
                                      mt: 1,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {meeting.Title}
                                  </Typography>
                                  <Box
                                    sx={{
                                      backgroundColor: statusColor,
                                      color: "white",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {status}
                                  </Box>
                                </Box>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Meeting ID:</strong>{" "}
                                  {meeting.MeetingId}
                                  {hasPermission(
                                    userPermissions,
                                    PERMISSION?.MODULE.MEETING_MANAGEMENT,
                                    PERMISSION?.ACTION?.COPY_MEETING_ID
                                  ) && (
                                      <Tooltip title="Copy Meeting Id" arrow>
                                        <IconButton
                                          size="small"
                                          sx={{ ml: 1 }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyMeetingId(meeting);
                                          }}
                                          aria-label="Copy Meeting ID"
                                        >
                                          <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Date:</strong>{" "}
                                  {formatLocalDate(
                                    meeting.FromDate,
                                    meeting.StartTime
                                  )}{" "}
                                  -{" "}
                                  {formatLocalDate(
                                    meeting.ToDate,
                                    meeting.EndTime
                                  )}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Time:</strong>{" "}
                                  {formatMeetingTime(meeting)}
                                </Typography>
                                <Typography variant="body2" noWrap>
                                  <strong>Description:</strong> {meeting.Agenda}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                }}
              </FixedSizeList>
            )}
          </AutoSizer>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
